import upperFirst from 'lodash/upperFirst';
import { InstitutionProfileModel } from '../models/institutionProfile.model';

interface IsEqualOptionsModel {
  caseInsensitive?: boolean;
}

class TextUtil {
  getGenitiveName(name: string) {
    if (typeof name !== 'string' || name.length < 1) {
      return name;
    }
    let suffix = 's';
    const lastCharacter = name.charAt(name.length - 1).toLowerCase();

    if (lastCharacter === 's' || lastCharacter === 'z') {
      suffix = "'";
    }

    return name + suffix;
  }

  isEqual(a: string, b: string, options: IsEqualOptionsModel = {}) {
    let sensitivity = 'case';
    if (options.caseInsensitive === true) {
      sensitivity = 'accent';
    }

    return a.localeCompare(b, undefined, { sensitivity }) === 0;
  }

  isPhoneNumber(value) {
    if (!value) {
      return true;
    }
    return /^[0-9\-\(\)\/\+\s]{8,15}$/.test(value);
  }

  /**
   * Test the value against a regex pattern for a basic email format.
   * The regex checks for:
   * - Alphanumeric characters, dots, or underscores before the '@'
   * - Alphanumeric characters, hyphens, and dots in the domain name
   * - A domain extension of at least two alphabetic characters
   * - Only ASCII characters throughout the email (no special Unicode characters)
   */
  isEmail(value) {
    if (!value) {
      return true;
    }
    return /^[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  }

  /**
   * Get a short name from a full name, taking the first character of each part of the name.
   *
   * @param fullName - The full name to shorten.
   * @returns A short name.
   */
  getShortName(fullName: string): string {
    const nameParts = fullName.trim().split(/[\s.]+/);
    let shortName = '';

    for (let i = 0; i < nameParts.length && shortName.length < 3; i++) {
      shortName += nameParts[i].charAt(0).toUpperCase();
    }

    return shortName;
  }

  /**
   * Get the text with first letter uppercase.
   *
   * @param text - The full name to shorten.
   * @returns First letter uppercase.
   */
  upperFirst(text: string): string {
    return upperFirst(text);
  }

  splitFileName(filename: string): { name: string; extension: string } {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return { name: filename, extension: '' }; // no extension
    }
    const name = filename.slice(0, lastDotIndex);
    const extension = filename.slice(lastDotIndex + 1);
    return { name, extension };
  }

  stripHtmlFromText(html: string | null | undefined): string {
    if (!html) {
      return '';
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  getDisplayName(institutionProfile: InstitutionProfileModel): string {
    const names = [];
    if (institutionProfile.fullName != null) {
      names.push(institutionProfile.fullName);
    } else if (institutionProfile.name != null) {
      names.push(institutionProfile.name);
    } else {
      names.push(institutionProfile.firstName, institutionProfile.lastName);
    }
    let displayName = names.join(' ');
    if (institutionProfile.alias) {
      displayName += ' (alias)';
    }
    if (institutionProfile.metadata) {
      displayName += ' (' + institutionProfile.metadata + ')';
    }
    return displayName;
  }
}

export const textUtil = new TextUtil();
